import Vue from 'vue'
import {
  EBatchType,
  ERecipientType,
  EResidentStatus, ESortOrder,
  EStatementStatuses,
  EUserPermissions,
  IResident,
  IResidentContact,
  IStatement,
  ITemplate
} from '@zunta/configurations'
import { CommentModule } from '@/store/modules/comment.module'
import { getModule } from 'vuex-module-decorators'
import { STATEMENT_MAILED_STATUSES } from '@/constants'
import { StatementModule } from '@/store/modules/statement.module'
import { ResidentModule } from '@/store/modules/resident.module'
import { TemplateModule } from '@/store/modules/template.module'
import { ETemplateType } from '@zunta/configurations/dist/types/template'
import Component from 'vue-class-component'
import { BatchModule } from '@/store/modules/batch.module'
import { User } from '@/classes/User'
import Api from '@/api'
import { NoCache } from '@/decorators/NoCache'

@Component
export default class shouldConfirmMailSharedFunctions extends Vue {
    batchType: EBatchType = EBatchType.STATEMENTS
    EBatchType = EBatchType
    EStatementStatuses = EStatementStatuses
    ERecipientType = ERecipientType
    EResidentStatus = EResidentStatus
    user: User = Api.getUser() as User

    statementModuleInstance: StatementModule = getModule(StatementModule, this.$store)
    batchModuleInstance: BatchModule = getModule(BatchModule, this.$store)
    commentModuleInstance: CommentModule = getModule(CommentModule, this.$store)
    residentModuleInstance: ResidentModule = getModule(ResidentModule, this.$store)
    templateModuleInstance: TemplateModule = getModule(TemplateModule, this.$store)
    disableStatusChangeBeforeStatsCalculated: boolean = false

    unwatch: any

    get isFcc () {
        return (this.selectedBatch && this.selectedBatch.facility && this.selectedBatch.facility.isFcc) || false
    }

    get showSuccessModal () {
        return this.$store.state.statement.showSuccessModal
    }

    get shouldConfirmMail () {
        return this.$store.state.statement.shouldConfirmMail
    }

    get previousStatementId () {
        return this.$store.state.statement.previousStatementId
    }

    get statementsType () {
        return this.$store.state.statement.statementsType
    }

    get needsReviewStatementList () {
        if (this.statementList && this.statementList.length) {
            return this.statementList.filter((statement: IStatement) => (
                statement.status === EStatementStatuses.NEEDS_REVIEW || statement.status === EStatementStatuses.NEEDS_FIX
            ))
        }
        return []
    }

    get selectedStatement (): IStatement | null {
        if (this.statementList && this.statementList.length) {
            return this.statementList.find((statement: IStatement) => (
                statement.id === parseInt(this.statementID, 10)
            ))
        }
        return null
    }

    get resident (): any {
        return this.selectedStatement ? this.selectedStatement.resident : null
    }

    @NoCache
    get statementID () {
        return this.$route.params.statement
    }

    getStatementStatus (statement: IStatement) {
        if (!statement) return ''
        return statement.status
    }

    get selectedStatementsList () {
        if (this.pageType === 'mailed') {
            return this.statementList
        }

        switch (this.statementsType) {
            case 'All' :
            case 'ALL' :
                return this.statementList
            default:
                if (typeof this.statementsType === 'string') {
                    return this.statementList.filter((statement: IStatement) =>
                        statement.status === this.statementsType)
                }
                return this.statementList.filter((statement: IStatement) =>
                    this.statementsType.includes(statement.status))
        }
    }

    get statementList () {
      if (!this.$store.state.statement.statementList || !this.$store.state.statement.statementList.length) {
        return []
      }

      const localList = [...this.$store.state.statement.statementList]
      // localList.sort((a: IStatement, b: IStatement) => {
      //   return (typeof a.resident === 'number' ? a.resident : a.resident.id) -
      //     (typeof b.resident === 'number' ? b.resident : b.resident.id)
      // })
      return localList
    }

    get batchID () {
        return this.$route.params.batch
    }

    get statistics () {
        return this.$store.state.batch.statementBatchStatistics
            ? this.$store.state.batch.statementBatchStatistics
            : {
                approved: 0,
                needsReviewAndFix: 0,
                excluded: 0,
                readyToMail: 0,
                readyToEmail: 0,
                readyToPrint: 0,
                doNotBillZero: 0,
                returned: 0,
                doNotBillNow: 0,
                emailReturned: 0,
                needsReview: 0,
                needsFix: 0,
                doNotBillPermanently: 0,
                doNotBillControl: 0,
                doNotBill: 0,
                doNotBillNegative: 0,
                doNotBillReturned: 0,
                doNotBillEmailReturned: 0,
                alreadySent: 0
            }
    }

    get pageType () {
        return this.$route.params.type || 'unmailed'
    }

    get isPcc () {
        return (this.selectedBatch && this.selectedBatch.facility && this.selectedBatch.facility.origin === 'PCC') || false
    }

    get readyToMailStatementsCount () {
        return this.$store.state.statement.statementList.filter((statement: IStatement) => (
            statement.status === EStatementStatuses.READY_TO_MAIL ||
            statement.status === EStatementStatuses.READY_TO_PRINT ||
            statement.status === EStatementStatuses.READY_TO_EMAIL
        )).length
    }

    get statementListTotal () {
        return this.$store.state.statement.statementListTotal
    }

    get currentStatementNum () {
        return this.selectedStatementsList.findIndex((statement: IStatement) =>
            statement.id === parseInt(this.statementID, 10)) + 1
    }

    get commentList () {
        return this.$store.state.comment.comments
    }

    get selectedBatch () {
        return this.$store.state.batch.selectedBatch
    }

    get statementMessages () {
        return this.$store.state.statement.statementMessages
    }

    get ableToSeePrevStatement () {
        return this.currentStatementNum !== 1
    }

    get ableToSeeNextStatement () {
        return this.currentStatementNum !== this.selectedStatementsList.length
    }

    get ableToSeePrevResident () {
        const newIndex = (this.calcCurrentResidentStatementsIndexes()[0] || 0) - 1
        return !isNaN(newIndex) && this.selectedStatementsList[newIndex]
    }

    get ableToSeeNextResident () {
        const newIndex = (this.calcCurrentResidentStatementsIndexes().pop() || 0) + 1
        return !isNaN(newIndex) && this.selectedStatementsList[newIndex]
    }

    get currentResidentStatementsIndexes () {
        return this.selectedStatementsList.flatMap((item: any, index: number) =>
            ((this.selectedStatement as IStatement).resident as IResident).id === item.resident.id ? [index] : [] as number[])
    }

    get templateOptionsList () {
        return this.$store.state.template.templates
    }

    get lettersList () {
        return this.templateOptionsList
            .filter((template: ITemplate) => template.type === ETemplateType.LETTER_PDF || template.type === ETemplateType.LETTER)
    }

    get messagesList () {
        return this.templateOptionsList
            .filter((template: ITemplate) => template.type === ETemplateType.MESSAGE)
    }

    get lettersListOptions () {
        return this.lettersList.map((template: ITemplate) => {
            return {
                key: template.id,
                value: template.id,
                content: template.templateName
            }
        })
    }

    get messagesListOptions () {
        return this.messagesList.map((template: ITemplate) => {
            return {
                key: template.id,
                value: template.id,
                content: template.templateName
            }
        })
    }

    get residentContacts (): IResidentContact[] {
        return this.$store.state.resident.residentContacts
    }

    get selectedForMailingResidentContacts (): IResidentContact[] {
        return this.residentContacts.filter((contact: IResidentContact) => contact.isEmailUsedForMailing || contact.isAddressUsedForMailing)
    }

    get batchStatements () {
        return this.$store.state.statement.batchStatements
    }

    get mailedStatuses () {
        return STATEMENT_MAILED_STATUSES
    }

    get currentStatementApproveStatus () {
        if (this.selectedStatement && this.selectedStatement.recipient) {
            // @ts-ignore
            switch (this.selectedStatement.recipient.type) {
                case ERecipientType.ROOM:
                    return this.EStatementStatuses.READY_TO_PRINT
                case ERecipientType.EMAIL:
                    return this.EStatementStatuses.READY_TO_EMAIL
                case ERecipientType.MAIL:
                default:
                    return this.EStatementStatuses.READY_TO_MAIL
            }
        }
        return this.EStatementStatuses.READY_TO_MAIL
    }

    calcCurrentResidentStatementsIndexes () {
        if (!this.selectedStatement) {
          return []
        }
        return (this.selectedStatementsList || []).flatMap((item: any, index: number) =>
            ((this.selectedStatement as IStatement).resident as IResident).id === item.resident.id ? [index] : []
        )
    }

    async changeStatementsType (type: EStatementStatuses | 'ALL') {
        this.$eventBus.$emit('statementReview:closeConfirmChangeStatus')
        this.commentModuleInstance.resetStatementComments()
        this.statementModuleInstance.updateStatementFilterType(type)
        try {
            await this.$router.push({
                name: 'review-statements',
                params: {
                    batch: this.batchID,
                    type: 'unmailed',
                    statement: this.selectedStatementsList[0] ? this.selectedStatementsList[0].id : 'nostatement'
                },
                query: {
                    defaultType: this.statementsType
                }
            })
        } finally {
            await this.updatedLogic()
        }
    }

    async updatedLogic () {
        this.$eventBus.$emit('loading:enable', { message: 'Wait, please' })
        if (this.selectedStatement && this.selectedStatement.id) {
            const presentInlist = this.selectedStatementsList.find((item: any) => item.id === (this.selectedStatement as IStatement).id)
            const presentForResident = this.selectedStatementsList.find((item: any) => ((this.selectedStatement as IStatement).resident as IResident) === item.resident)
            if (!presentInlist && presentForResident) {
                await this.nextStatement(presentForResident.id)
            } else if (!presentInlist && !presentForResident) {
                await this.nextStatement()
            }
        }
        if (this.selectedStatement && this.selectedStatement.id) {
            this.statementModuleInstance.setPreviousStatementId(this.selectedStatement.id)
            await Promise.all([
                this.commentModuleInstance.fetchStatementComments((this.selectedStatement as IStatement).id),
                this.statementModuleInstance.fetchStatementMessages({
                    statementId: ((this.selectedStatement as IStatement).resident as IResident).id,
                    batchId: this.batchID
                }),
                this.fetchContactsList(),
                this.getRelatedResidents()
            ])
        }
        this.$nextTick(() => this.$eventBus.$emit('loading:disable'))
    }

    async fetchData () {
        await this.batchModuleInstance.fetchBatch(this.batchID)
        this.batchType = this.selectedBatch.type
        await this.fetchList()

        if (!this.$route.params.type && this.$route.params.statement) {
            const stat = this.statementList.find((item: any) => item.id === Number(this.$route.params.statement))
            const type = stat && !this.mailedStatuses.includes(stat.status) ? 'unmailed' : 'mailed'
            await this.$router.push({
                name: 'review-statements',
                params: {
                    batch: this.batchID,
                    type: type,
                    statement: this.$route.params.statement
                },
                query: {
                    defaultType: this.statementsType
                }
            })
            if (type === 'mailed') {
                window.location.reload()
            }
        }

        if (this.pageType !== 'mailed') {
            if (this.needsReviewStatementList.length === 0 && this.$route.query.defaultType === EStatementStatuses.NEEDS_REVIEW) {
                this.statementModuleInstance.updateStatementFilterType('ALL')
            } else if (this.$route.query.defaultType) {
                // @ts-ignore
                this.statementModuleInstance.updateStatementFilterType(this.$route.query.defaultType as string)
            } else {
                this.statementModuleInstance.updateStatementFilterType('ALL')
            }

            if (!this.statementID) {
                await this.$router.push({
                    name: 'review-statements',
                    params: {
                        batch: this.batchID,
                        type: 'unmailed',
                        statement: this.selectedStatementsList[0] ? this.selectedStatementsList[0].id : 'nostatement'
                    },
                    query: {
                        // defaultType: 'ALL',
                        defaultType: this.$route.query.defaultType || 'ALL',
                        backUrl: this.$route.query.backUrl
                    }
                })
            }
            await this.updateStatistics()
            await this.updatedLogic()
        } else {
            await this.statementModuleInstance.fetchStatementById(this.statementID)
            await this.updatedLogic()
        }
    }

    async updateStatistics () {
        await this.batchModuleInstance.getStatementBatchStatistics(this.batchID)
    }

    goBack () {
        let batchId = this.selectedStatement ? this.selectedStatement.batch : this.$route.params.batch
        this.$locationManager.goBack({ name: 'review-statements', params: { batch: batchId } })
    }

    async getRelatedResidents (residentId?: number) {
        if (!this.selectedStatement && residentId) {
            const statement = this.selectedStatementsList.find((item: any) => item.resident.id === residentId)
            await this.changeStatement(statement ? statement.id : this.selectedStatementsList[0].id)
            return
        }
        await this.statementModuleInstance.fetchResidentBatchStatements({
            residentId: this.selectedStatement
                ? (this.selectedStatement.resident as IResident).id || this.selectedStatement.resident as number
                : residentId || -1,
            batchId: this.batchID
        })
    }

    async fetchList (type?: any) {
        await this.statementModuleInstance.getBatchStatementListByFilters({
            batchId: this.batchID,
            filters: {
                sortBy: 'residentName',
                sortOrder: ESortOrder.ASC,
                mailed: type || this.pageType
            }
        })
    }

    filterStatusType (status: string | string[]) {
        if (typeof status === 'string') {
            switch (status) {
                case EStatementStatuses.READY_TO_PRINT:
                    return 'readyToPrint'
                case EStatementStatuses.READY_TO_EMAIL:
                    return 'readyToEmail'
                case EStatementStatuses.READY_TO_MAIL:
                    return 'readyToMail'
                case EStatementStatuses.NEEDS_FIX:
                    return 'needsFix'
                case EStatementStatuses.NEEDS_REVIEW:
                    return 'needsReview'
                case EStatementStatuses.RETURNED:
                    return 'returned'
                case EStatementStatuses.EMAIL_RETURNED:
                    return 'emailReturned'
                case EStatementStatuses.DO_NOT_BILL_NOW:
                    return 'doNotBillNow'
                case EStatementStatuses.DO_NOT_BILL:
                    return 'doNotBill'
                case EStatementStatuses.DO_NOT_BILL_PERMANENTLY:
                    return 'doNotBillPermanently'
                case EStatementStatuses.DO_NOT_BILL_ZERO:
                    return 'doNotBillZero'
                case EStatementStatuses.DO_NOT_BILL_CONTROL:
                    return 'doNotBillControl'
                case EStatementStatuses.DO_NOT_BILL_NEGATIVE:
                    return 'doNotBillNegative'
                case EStatementStatuses.DO_NOT_BILL_RETURNED:
                  return 'doNotBillReturned'
                case EStatementStatuses.DO_NOT_BILL_EMAIL_RETURNED:
                  return 'doNotBillEmailReturned'
                case 'STOP_BILLING':
                    return 'needsReviewAndFix'
                case EStatementStatuses.ALREADY_SENT:
                    return 'alreadySent'
            }
            return null
        }
        if (status === [
            EStatementStatuses.NEEDS_REVIEW,
            EStatementStatuses.NEEDS_FIX,
            EStatementStatuses.RETURNED,
            EStatementStatuses.EMAIL_RETURNED
        ]) {
            return 'needsReviewAndFix'
        }
        if (status === [
            EStatementStatuses.DO_NOT_BILL_NOW,
            EStatementStatuses.DO_NOT_BILL_NEGATIVE,
            EStatementStatuses.DO_NOT_BILL_ZERO,
            EStatementStatuses.DO_NOT_BILL_CONTROL,
            EStatementStatuses.DO_NOT_BILL_PERMANENTLY,
            EStatementStatuses.DO_NOT_BILL_RETURNED,
            EStatementStatuses.DO_NOT_BILL_EMAIL_RETURNED
        ]) {
            return 'excluded'
        }
        if (status === [
            EStatementStatuses.READY_TO_MAIL,
            EStatementStatuses.READY_TO_EMAIL,
            EStatementStatuses.READY_TO_PRINT
        ]) {
            return 'approved'
        }
        return null
    }

    async fetchTemplates () {
        await this.templateModuleInstance.fetchTemplates({})
    }

    async setSendingStatementsForResident (isStopped: boolean) {
        if (!this.selectedStatement) {
            return
        }

        if (!this.user.hasPermissionFor(EUserPermissions.EDIT_RESIDENT_SETTINGS)) {
            this.$eventBus.$emit('notify', {
                color: 'var(--status-error-color)',
                message: 'Permission Denied',
                header: `Modify Resident Settings`,
                icon: 'warning'
            })
            return
        }

        const nextStatement = this.selectedStatementsList[this.currentStatementNum] || {}

        this.statementModuleInstance.setStopBillingModalState(false)
        this.$eventBus.$emit('loading:enable', { message: 'Update Status' })

        const response = await this.residentModuleInstance.updateResidentSettings({
            residentId: (this.selectedStatement.resident as IResident).id,
            settings: { stopSendingStatements: isStopped },
            affectedBatch: Number(this.batchID)
        })
        if (response !== 'OK') {
          this.$eventBus.$emit('notify', {
            color: 'var(--status-error-color)',
            message: Array.isArray(response.message) ? response.message.join('\n') : response.message,
            header: `Statements Exclusion`,
            icon: 'warning'
          })
          this.$eventBus.$emit('loading:disable')
          return
        }

        // Changing status to excluded for all user statements in current batch
        const statusToSet: EStatementStatuses = isStopped ? EStatementStatuses.DO_NOT_BILL_PERMANENTLY : EStatementStatuses.NEEDS_REVIEW
        switch (statusToSet) {
            case EStatementStatuses.NEEDS_REVIEW:
                this.$eventBus.$emit('notify', {
                    color: 'var(--status-incomplete-color)',
                    message: 'In Needs Review status',
                    header: `#${this.selectedStatement.number || ''}`,
                    icon: 'warning'
                })
                break
            case EStatementStatuses.DO_NOT_BILL_PERMANENTLY:
                this.$eventBus.$emit('notify', {
                    color: 'var(--status-progress-color)',
                    message: 'In Permanently Excluded status',
                    header: `#${this.selectedStatement.number || ''}`,
                    icon: 'exclude'
                })
                break
        }

        await Promise.all([
            this.batchModuleInstance.fetchBatch(this.batchID),
            this.fetchList(),
            this.updateStatistics(),
            this.commentModuleInstance.fetchStatementComments((this.selectedStatement as IStatement).id),
            this.statementModuleInstance.fetchStatementMessages({
                statementId: ((this.selectedStatement as IStatement).resident as IResident).id,
                batchId: this.batchID
            }),
            this.fetchContactsList(),
            this.getRelatedResidents()
        ])
        this.$eventBus.$emit('loading:disable')

        if (this.ableToSeeNextStatement) {
            await this.nextStatement(nextStatement.id)
            return
        }
        await this.changeStatementsType('ALL')
    }

    async changeStatus (status: EStatementStatuses, withConfirm: boolean = false) {
        this.disableStatusChangeBeforeStatsCalculated = true
        if (!this.selectedStatement) {
            this.disableStatusChangeBeforeStatsCalculated = false
            return
        }
        if (withConfirm && ![
            EStatementStatuses.NEEDS_REVIEW,
            EStatementStatuses.READY_TO_EMAIL,
            EStatementStatuses.READY_TO_MAIL,
            EStatementStatuses.READY_TO_PRINT
        ].includes(this.selectedStatement.status as any)) {
            this.$eventBus.$emit('statementReview:confirmChangeStatus', this.selectedStatement.status)
            this.disableStatusChangeBeforeStatsCalculated = false
            return
        }

        if (this.resident.status === EResidentStatus.INACTIVE &&
            (this.selectedStatement as IStatement).recipient && ((this.selectedStatement as IStatement).recipient as any).type === 'ROOM' &&
            this.currentStatementApproveStatus === status
        ) {
            this.disableStatusChangeBeforeStatsCalculated = false
            return
        }

        const currNum = this.currentStatementNum
        const statementId = this.selectedStatement.id
        const nextStatement = this.selectedStatementsList[this.currentStatementNum]

        const payload = {
            statementId,
            status: status as EStatementStatuses,
            batch: parseInt(this.batchID, 10)
        }

        if (
          ((this.selectedStatement as IStatement).status === EStatementStatuses.RETURNED &&
            status === EStatementStatuses.DO_NOT_BILL)
        ) {
          payload.status = EStatementStatuses.DO_NOT_BILL_RETURNED
        }

        if (
          ((this.selectedStatement as IStatement).status === EStatementStatuses.EMAIL_RETURNED) &&
          status === EStatementStatuses.DO_NOT_BILL
        ) {
          payload.status = EStatementStatuses.DO_NOT_BILL_EMAIL_RETURNED
        }

        const response: any = await this.statementModuleInstance.changeStatementStatus(payload)

        if (response !== 'OK') {
            if (response && response.message && response.message.includes('Mailing Contact Is Invalid')) {
                await this.changeStatus(EStatementStatuses.NEEDS_FIX)
            }
            this.$eventBus.$emit('notify', {
                color: 'var(--status-error-color)',
                message: Array.isArray(response.message) ? response.message.join('\n') : response.message,
                header: `Approve Statement`,
                icon: 'warning'
            })
            this.disableStatusChangeBeforeStatsCalculated = false
            return
        }

        switch (status) {
            case EStatementStatuses.READY_TO_MAIL:
                this.$eventBus.$emit('notify', {
                    color: 'var(--status-success-color)',
                    message: 'In approve status',
                    header: `#${this.selectedStatement.number || ''}`,
                    icon: 'check'
                })
                break
            case EStatementStatuses.NEEDS_FIX:
                this.$eventBus.$emit('notify', {
                    color: 'var(--status-incomplete-color)',
                    message: 'In needs fix status',
                    header: `#${this.selectedStatement.number || ''}`,
                    icon: 'warning'
                })
                break
            case EStatementStatuses.DO_NOT_BILL:
            case EStatementStatuses.DO_NOT_BILL_PERMANENTLY:
            case EStatementStatuses.DO_NOT_BILL_NOW:
                this.$eventBus.$emit('notify', {
                    color: 'var(--status-progress-color)',
                    message: 'In Do Not Bill status',
                    header: `#${this.selectedStatement.number || ''}`,
                    icon: 'exclude'
                })
                break
        }
        await this.updateStatistics()
        this.disableStatusChangeBeforeStatsCalculated = false

        if (this.statistics.needsReviewAndFix === 0) {
          await this.statementModuleInstance.fetchMailStatements(this.batchID)

          this.$nextTick(() => {
              this.statistics.approved > 0
                  ? this.statementModuleInstance.setConfirmMailModalState(true)
                  : this.statementModuleInstance.setSuccessModalState(true)
          })
          await this.changeStatementsType('ALL')
          return
        }

        const type = this.filterStatusType(this.statementsType) || 'ALL'
        // @ts-ignore
        if (this.statistics[type] === 0) {
            await this.changeStatementsType('ALL')
            return
        }

        if (this.batchStatements && this.batchStatements.length) {
            if (status === EStatementStatuses.DO_NOT_BILL_PERMANENTLY) {
                const statement = this.selectedStatementsList.find((item: any, index: number) => {
                    // @ts-ignore
                    return item.resident.id !== this.selectedStatement.resident.id && index + 1 > currNum
                })
                await this.changeStatement(statement ? statement.id : nextStatement.id)
                return
            }

            // @ts-ignore
            const indexList = this.selectedStatementsList.flatMap((item: any, index: number) =>
              item.resident.id === ((this.selectedStatement as IStatement).resident as IResident).id ? index : [])

            if (this.$route.query.defaultType !== 'ALL' && indexList.length) {
                for (let i = 0; i < indexList.length; i++) {
                    if (this.selectedStatement.id !== this.selectedStatementsList[indexList[i]].id &&
                        this.selectedStatement.id > this.selectedStatementsList[indexList[i]].id &&
                        this.selectedStatementsList[indexList[i]].status !== this.$route.query.defaultType) {
                        await this.changeStatement(this.selectedStatementsList[indexList[i]].id)
                        return
                    }
                }
            } else {
                if (this.$route.params.type === 'unmailed') {
                    for (let i = 0; i < indexList.length; i++) {
                        if (this.selectedStatement.id !== this.selectedStatementsList[indexList[i]].id &&
                            this.selectedStatement.id < this.selectedStatementsList[indexList[i]].id &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_MAIL &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_EMAIL &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_PRINT &&
                            !this.statementsType.includes(this.mailedStatuses)) {
                            await this.changeStatement(this.selectedStatementsList[indexList[i]].id)
                            return
                        }
                    }
                } else {
                    for (let i = 0; i < indexList.length; i++) {
                        if (this.selectedStatement.id !== this.selectedStatementsList[indexList[i]].id &&
                            this.selectedStatement.id < this.selectedStatementsList[indexList[i]].id &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_MAIL &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_EMAIL &&
                            this.selectedStatementsList[indexList[i]].status !== EStatementStatuses.READY_TO_PRINT) {
                            await this.changeStatement(this.selectedStatementsList[indexList[i]].id)
                            return
                        }
                    }
                }
            }
            // }
        } else {
            await this.changeStatementsType('ALL')
        }

        if (this.ableToSeeNextStatement) {
            if (nextStatement && nextStatement.id) {
                await this.changeStatement(nextStatement.id)
                return
            }
            await this.nextStatement()
            return
        }

        if (this.selectedStatementsList[0]) {
            await this.changeStatement(this.selectedStatementsList[0].id)
            return
        }
        await this.changeStatementsType('ALL')
    }

    async nextStatement (statementId?: number) {
        if (this.selectedStatementsList[this.currentStatementNum]) {
            this.$eventBus.$emit('statementReview:closeConfirmChangeStatus')
            this.commentModuleInstance.resetStatementComments()
            await this.$router.push({
                name: 'review-statements',
                params: {
                    batch: this.batchID,
                    type: 'unmailed',
                    statement: statementId || this.selectedStatementsList[this.currentStatementNum].id
                },
                query: {
                    defaultType: this.statementsType
                }
            })
        }
    }

    async changeStatement (statement: any) {
        this.$eventBus.$emit('statementReview:closeConfirmChangeStatus')
        if (parseInt(this.$route.params.statement) !== parseInt(statement) ||
            parseInt(this.$route.params.batch) !== parseInt(this.batchID) ||
            this.$route.query.defaultType !== this.statementsType
        ) {
            this.commentModuleInstance.resetStatementComments()
        }

        const stat = this.statementList.find((item: any) => item.id === statement)
        let type = stat && !this.mailedStatuses.includes(stat.status) ? 'unmailed' : 'mailed'
        if (!stat) {
            type = this.pageType === 'unmailed' ? 'mailed' : 'unmailed'
        }

        if (type !== this.pageType) {
            this.$eventBus.$emit('loading:enable', { message: 'Wait, please' })
            await this.fetchList(type)
        }
        try {
            await this.$router.push({
                name: 'review-statements',
                params: {
                    batch: this.batchID,
                    type,
                    statement
                },
                query: {
                    defaultType: this.statementsType
                }
            })
        } finally {
            this.$eventBus.$emit('loading:enable', { message: 'Wait, please' })
            this.selectedStatement && await Promise.all([
                async () => type === 'unmailed' && this.updateStatistics(),
                this.commentModuleInstance.fetchStatementComments((this.selectedStatement as IStatement).id),
                this.statementModuleInstance.fetchStatementMessages({
                    statementId: ((this.selectedStatement as IStatement).resident as IResident).id,
                    batchId: this.batchID
                }),
                this.fetchContactsList(),
                this.getRelatedResidents()
            ])
            this.$forceUpdate()
            setTimeout(() => this.$eventBus.$emit('loading:disable'), 300)
        }
    }

    async fetchContactsList () {
        if (this.selectedStatement) {
            await this.residentModuleInstance.fetchResidentContacts((this.selectedStatement.resident as IResident).id)
        }
    }

    async prevResident () {
        const indexes = this.calcCurrentResidentStatementsIndexes()
        const list = this.selectedStatementsList
        let newIndex = indexes[0] - 1
        if (list[newIndex]) {
            const statements = list.filter((item: any) => item.resident.id === list[newIndex].resident.id)
            const minValue = statements.reduce((min: any, p: any) => p.id < min ? p.id : min, list[newIndex].id)
            newIndex = list.findIndex((item: any) => item.id === minValue)
        }
        if (!isNaN(newIndex) && list[newIndex]) {
            const type = this.mailedStatuses.includes(list[newIndex].status) ? 'mailed' : 'unmailed'
            // XXX TODO rewrite
            if (this.pageType !== type) {
                let calcIndex = 0
                for (let i = newIndex; i > 0; i--) {
                    if (calcIndex > 0) {
                        break
                    }
                    const calculatedType = this.mailedStatuses.includes(list[i].status) ? 'mailed' : 'unmailed'
                    if (calculatedType === this.pageType) {
                        calcIndex = i
                    }
                }
                newIndex = calcIndex
                if (!newIndex) {
                    return
                }
            }
            await this.changeStatement(list[newIndex].id)
        }
    }

    async nextResident () {
        const indexes = this.calcCurrentResidentStatementsIndexes()
        let newIndex = (indexes.pop() || 0) + 1
        const list = this.selectedStatementsList
        if (!isNaN(newIndex) && list[newIndex]) {
            const type = this.mailedStatuses.includes(list[newIndex].status) ? 'mailed' : 'unmailed'
            // XXX TODO rewrite
            if (this.pageType !== type) {
                let calcIndex = 0
                for (let i = newIndex; i < list.length; i++) {
                    if (calcIndex > 0) {
                        continue
                    }
                    const calculatedType = this.mailedStatuses.includes(list[i].status) ? 'mailed' : 'unmailed'
                    if (calculatedType === this.pageType) {
                        calcIndex = i
                    }
                }
                newIndex = calcIndex
                if (!newIndex) {
                    return
                }
            }
            await this.changeStatement(list[newIndex].id)
            return
        }
        await this.nextStatement()
    }
}
